import { AuthStatus, SmsStatus } from 'feature/auth/redux/authSlice'
import {
  useSendCompanyCodeMutation,
  useSendMagicLinkMutation,
  useVerifyCompanyCodeMutation,
} from 'feature/company/redux/company.api'
import { useAddressSliceActions } from 'feature/addresses/hooks/useAddressSliceActions'
import { phones } from 'helpers/phones'
import { MainLayout } from 'layouts'
import { FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActions, useAppSelector } from 'store/hooks'
import { SmsCodeConfirm } from 'widget'
import { Severity } from 'widget/Notifications'

import { CompanyPhoneForm } from './CompanyPhoneForm'
import { MagicLinkEmailForm } from './MagicLinkEmailForm'
import { MagicLinkSentInfo } from './MagicLinkSentInfo'

import { EMAIL_REGEX } from 'constants/validation'

type Step = 'email' | 'emailSent' | 'phone' | 'sms'

export function CompanyLogin() {
  const navigate = useNavigate()
  const {
    addNotification,
    setSmsInfo,
    setAuthInfo,
    setUserInfo,
    clearAddress,
    clearMap,
  } = useActions()
  const companyAddressActions = useAddressSliceActions('company')
  const { userInfo, smsInfo } = useAppSelector(state => state.auth)

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [hasError, setHasError] = useState(false)
  const [errorText, setErrorText] = useState<string | null>(null)
  const [blockedTime, setBlockedTime] = useState(0)

  const [sendMagicLink, { isLoading: isMagicLinkLoading }] =
    useSendMagicLinkMutation()
  const [sendCompanyCode] = useSendCompanyCodeMutation()
  const [verifyCompanyCode, { isLoading: isVerifyLoading }] =
    useVerifyCompanyCodeMutation()

  const isEmailValid = EMAIL_REGEX.test(email.trim())
  const hasForbiddenComTld = (checkedEmail: string): boolean =>
    checkedEmail.trim().toLowerCase().endsWith('.com')

  // === EMAIL STEP ===

  const handleBlur = () => {
    if (email.trim() && !isEmailValid) {
      setHasError(true)
    }
  }

  const handleChange = (e: { target: { value: string } }) => {
    setEmail(e.target.value)
    setHasError(false)
    setErrorText(null)
  }

  const submitHandler = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!isEmailValid || hasForbiddenComTld(email)) return

    try {
      const result = await sendMagicLink({ email: email.trim() }).unwrap()

      if (result.isRegistrationNeeded) {
        setStep('phone')
        return
      }

      setBlockedTime(result.blockedTime)
      setStep('emailSent')
    } catch (err: any) {
      const message = err?.data?.message
      if (message) {
        addNotification({ text: message, severity: Severity.error })
      } else {
        addNotification({
          text: 'Произошла ошибка. Попробуйте позже',
          severity: Severity.error,
        })
      }
    }
  }

  const handleResend = async () => {
    try {
      const result = await sendMagicLink({ email: email.trim() }).unwrap()
      setBlockedTime(result.blockedTime)
    } catch {
      setErrorText('Не удалось отправить письмо повторно')
    }
  }

  // === SMS STEP ===

  const handleSmsBack = () => {
    setSmsInfo({ status: SmsStatus.readyForSend, error: null })
    setStep('phone')
  }

  const handleReRequestSms = async () => {
    const formattedPhone = `+${phones(userInfo.phone)}`
    try {
      const data = await sendCompanyCode({ phone: formattedPhone }).unwrap()
      setSmsInfo({
        error: null,
        blockTime: data.retryAfter,
      })
    } catch {
      // ошибка обрабатывается RTK Query
    }
  }

  const handleCodeSubmit = async (code: string, phoneNumber: string) => {
    const formattedPhone = `+${phones(phoneNumber)}`
    try {
      const data = await verifyCompanyCode({
        phone: formattedPhone,
        code,
      }).unwrap()
      window.ym?.(107075503, 'reachGoal', 'form_200')
      setAuthInfo({
        status: AuthStatus.smsConfirmedCompany,
        accessToken: data.registrationToken,
      })
      const previousEmail = sessionStorage.getItem('companyRegistrationEmail')
      if (previousEmail && previousEmail !== email.trim()) {
        sessionStorage.removeItem('companyRegistrationForm')
        sessionStorage.removeItem('companyRegistrationStep')
        sessionStorage.removeItem('companyRegistrationSameAsLegal')
        sessionStorage.removeItem('companyRegistrationDocFiles')
        sessionStorage.removeItem('inputSuggest')
        sessionStorage.removeItem('companyInputSuggest')
        sessionStorage.removeItem('yandex_map_clickMark')
        clearAddress()
        companyAddressActions.clearAddress()
        clearMap()
      }
      sessionStorage.setItem('companyRegistrationEmail', email.trim())
      sessionStorage.setItem('companyRegistrationPhone', userInfo.phone)
      setUserInfo({ phone: '' })
      navigate('/company/registration')
    } catch (err: any) {
      const message = err?.data?.message
      setSmsInfo({ error: message || 'Неверный код' })
    }
  }

  // === RENDER ===

  const renderStep = () => {
    switch (step) {
      case 'emailSent':
        return (
          <MagicLinkSentInfo
            email={email}
            isLoading={isMagicLinkLoading}
            blockedTime={blockedTime}
            onResend={handleResend}
            onChangeEmail={() => setStep('email')}
          />
        )
      case 'phone':
        return (
          <CompanyPhoneForm
            onBack={() => setStep('email')}
            onCodeSent={() => setStep('sms')}
          />
        )
      case 'sms':
        return (
          <SmsCodeConfirm
            title='Подтверждение телефона'
            startTimerPosition={smsInfo.blockTime}
            backHandler={handleSmsBack}
            phone={userInfo.phone}
            onReRequestSms={handleReRequestSms}
            onCodeSubmit={handleCodeSubmit}
            errorText={smsInfo.error}
            onChange={() => setSmsInfo({ error: null })}
            isButtonLoading={isVerifyLoading}
          />
        )
      default:
        return (
          <MagicLinkEmailForm
            email={email}
            hasError={hasError}
            errorText={errorText}
            isEmailValid={isEmailValid}
            isLoading={isMagicLinkLoading}
            onChange={handleChange}
            onBlur={handleBlur}
            onSubmit={submitHandler}
            hasForbiddenComTld={hasForbiddenComTld}
          />
        )
    }
  }

  return (
    <MainLayout>
      <div className='container'>{renderStep()}</div>
    </MainLayout>
  )
}
